import { create } from 'zustand';
import { applyDocumentLocale } from '@/lib/locale-direction';

/** Langues supportées par l’app (aligné sur i18n `supportedLngs`). */
export type Language = 'fr' | 'en' | 'lb' | 'it' | 'es' | 'uk' | 'ru' | 'pt' | 'de' | 'ar' | 'tr';

const SUPPORTED_LANGUAGE_CODES = ['fr', 'en', 'lb', 'it', 'es', 'uk', 'ru', 'pt', 'de', 'ar', 'tr'] as const;

function isSupportedLanguage(code: string): code is Language {
  return (SUPPORTED_LANGUAGE_CODES as readonly string[]).includes(code);
}

/**
 * Même logique pour l’état initial du store et pour i18next (`lng`) :
 * évite le décalage (ex. navigateur en anglais → header marketing en EN mais centre d’aide resté en FR).
 */
/** Langue par défaut si la préférence utilisateur / navigateur n’est pas dans {@link SUPPORTED_LANGUAGE_CODES}. */
const FALLBACK_LANGUAGE: Language = 'en';

export function resolveInitialLanguage(): Language {
  if (typeof window === 'undefined') {
    return FALLBACK_LANGUAGE;
  }

  try {
    const app = localStorage.getItem('app_language');
    if (app && isSupportedLanguage(app)) {
      return app;
    }
    const rawLocale = localStorage.getItem('locale');
    const locCode = rawLocale?.split('-')[0] ?? '';
    if (locCode && isSupportedLanguage(locCode)) {
      return locCode;
    }
  } catch (error) {
    console.warn('Error reading language from localStorage:', error);
  }

  const browserLang =
    typeof navigator !== 'undefined' && navigator.language
      ? navigator.language.split('-')[0]
      : '';
  if (browserLang && isSupportedLanguage(browserLang)) {
    return browserLang;
  }

  return FALLBACK_LANGUAGE;
}

// Inline setStoredLanguage to avoid circular dependency
function setStoredLanguage(language: Language): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem('app_language', language);
  } catch (error) {
    console.warn('Error saving language to localStorage:', error);
  }
}

interface LanguageState {
  language: Language;
  setLanguage: (lang: Language) => void;
}

export const useLanguageStore = create<LanguageState>((set) => ({
  language: resolveInitialLanguage(),
  setLanguage: (lang: Language) => {
    setStoredLanguage(lang);
    applyDocumentLocale(lang);
    // Update i18n instance if available (loaded dynamically elsewhere)
    if (typeof window !== 'undefined' && (window as any).__i18n) {
      (window as any).__i18n.changeLanguage(lang).catch(() => {});
    }
    set({ language: lang });
  },
}));
